import { FC, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';

import { buttonVariants, buttonWidths } from '@clatter/ui';
import {
  fetchSystemProfile,
  formatDate,
  platformSelectors,
  requestStatus,
  systemSettingsApi,
  useDeepCompareMemo,
} from '@clatter/platform';
import { config } from '../../../../config';
import { addNotification, authSelectors } from '../../../../store';
import { useUpdateDocumentMutation } from '../../../../services';
import {
  AccessList,
  NgButton,
  NgCard,
  NgCardContent,
  NgCardTitle,
  NgErrorMessage,
  NgInputCopyButton,
} from '../../../../components';

import { TemplateLabel } from './TemplateLabel';
import { PublishWebsitePanel, PublishWebsiteAccessOption, PublishWebsiteNavOption } from './PublishWebsitePanel';
import { GlobeIcon, LinkIcon, LockIcon } from '../../../UI/NgIcons';

const emailPattern = /^\s*([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}|[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})\s*$/;
const slugPattern = /^[a-zA-Z0-9_/]+$/;

const NavType = {
  none: 'none',
  customPage: 'custom_page',
  homepage: 'homepage',
} as const;

const AccessType = {
  public: 'public',
  customUrl: 'custom_url',
  privateLink: 'private_link',
  protected: 'protected',
} as const;

const getSitesDomain = () => {
  try {
    return new URL(config.ngSitesBaseUrl).host;
  } catch {
    return 'localhost:3011';
  }
};

const generatePrivateToken = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes, (b) => chars[b % chars.length]).join('');
};

type CustomPageEntry = { ID: number; url: string; DisplayName: string };

interface MicrositesPublishSectionProps {
  projectData: any;
  isQueryValid: boolean;
  isQueriesEnabled: boolean;
  isLoading: boolean;
  buttonOnly?: boolean;
  panelAnchor?: 'left' | 'right';
  attributes?: { [key: string]: string }[];
}

interface MicrositeListItem {
  id: number;
  name: string;
  type: string;
  previewLink?: string;
  siteUrl?: string;
  lastUpdated: string;
  queryID?: number;
  microsite: {
    emailWhitelist: string[];
    published: boolean;
    publishedAt: string | null;
    protected: boolean;
    customUrl?: boolean;
    privateLink?: boolean;
    slug?: string;
  };
}

const StyledMicrositeRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;
`;

const StyledMicrositeLabelMeta = styled.div`
  display: flex;
  flex-direction: column;
  font-size: ${({ theme }) => theme.fontSize.default};
`;

const StyledButtonOnlyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  gap: 6px;
`;

const StyledButtonOnlyMeta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;

  small {
    font-size: ${({ theme }) => theme.fontSize.xs};
    color: ${({ theme }) => theme.palette.textMuted};
  }
`;

const StyledLinkLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.textMedium};
  margin-bottom: 6px;
`;

const StyledLinkBox = styled.div`
  display: flex;
  align-items: stretch;
  border: 1.5px solid ${({ theme }) => theme.palette.inputBorder};
  border-radius: 7px;
  overflow: hidden;
`;

const StyledLinkPrefix = styled.span`
  font-size: 11px;
  font-family: monospace;
  flex-shrink: 0;
  padding: 8px 10px;
  white-space: nowrap;
  color: ${({ theme }) => theme.palette.textMuted};
  background: ${({ theme }) => theme.palette.cardSurface};
  border-right: 1.5px solid ${({ theme }) => theme.palette.inputBorder};
`;

const StyledLinkSlug = styled.span`
  font-size: 11px;
  font-family: monospace;
  color: ${({ theme }) => theme.palette.textMedium};
  background: ${({ theme }) => theme.palette.cardBackground};
  padding: 8px 10px;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledEmailInputRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
`;

const StyledEmailInput = styled.input`
  flex: 1;
  font-size: 11px;
  padding: 8px 10px;
  outline: none;
  font-family: inherit;
  background: ${({ theme }) => theme.palette?.inputBackground};
  border: 1.5px solid ${({ theme }) => theme.palette?.inputBorder};
  border-radius: ${({ theme }) => theme.radius?.input};
  color: ${({ theme }) => theme.palette?.text};

  &:focus {
    border-color: ${({ theme }) => theme.palette.overlayAccent};
  }
  &::placeholder {
    color: ${({ theme }) => theme.palette?.inputPlaceholder};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StyledSlugInput = styled.input`
  flex: 1;
  min-width: 0;
  font-size: 11px;
  font-family: monospace;
  padding: 8px 10px;
  outline: none;
  background: ${({ theme }) => theme.palette?.cardBackground};
  border: none;
  color: ${({ theme }) => theme.palette?.textMedium};

  &:focus {
    outline: none;
  }
  &::placeholder {
    color: ${({ theme }) => theme.palette?.inputPlaceholder};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StyledAddButton = styled.button`
  cursor: pointer;
  border: none;
  padding: 6px 14px;
  font-weight: 600;
  font-family: inherit;
  white-space: nowrap;
  background: ${({ theme }) => theme.palette?.linkHoverColor};
  color: ${({ theme }) => theme.palette?.white};
  border-radius: ${({ theme }) => theme.radius?.input};
  font-size: ${({ theme }) => theme.fontSize?.sm};

  &:hover {
    opacity: 0.85;
  }
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const StyledHint = styled.div`
  font-size: 10px;
  color: ${({ theme }) => theme.palette.textMuted};
  margin-top: 6px;
  line-height: 1.4;
`;

const StyledSlugError = styled.div`
  font-size: 10px;
  color: #dc2626;
  margin-top: 4px;
`;

export const MicrositesPublishSection: FC<MicrositesPublishSectionProps> = ({
  projectData,
  isLoading: isParentLoading,
  isQueryValid,
  isQueriesEnabled,
  buttonOnly = false,
  panelAnchor = 'right',
}) => {
  const dispatch = useDispatch();
  const {
    landingPageUrl,
    customPages,
    loadingStatus: systemProfileLoadingStatus,
  } = useSelector(platformSelectors.systemProfile.selectSystemProfile);

  const isAdminUser = useSelector(authSelectors.isAdmin);

  const [panelItemId, setPanelItemId] = useState<number | null>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [emailWhitelist, setEmailWhitelist] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState('');
  const [micrositeSlug, setMicrositeSlug] = useState('');
  const [slugError, setSlugError] = useState<string | null>(null);

  const [updateDocument, { isLoading: isUpdateDocumentLoading }] = useUpdateDocumentMutation();

  const isLoading = isParentLoading || isUpdateDocumentLoading || systemProfileLoadingStatus === requestStatus.pending;

  const micrositesList: MicrositeListItem[] = useDeepCompareMemo(() => {
    return (projectData?.Documents || [])
      .map((item: any) => {
        if ((item?.DocumentTemplate?.AllowedOutputTypes || []).includes('html')) {
          const microsite = JSON.parse(item?.Microsite || '{}');
          const siteUrl =
            microsite.customUrl && microsite.slug
              ? `${config.ngSitesBaseUrl}/${microsite.slug}`
              : microsite.privateLink && microsite.slug
              ? `${config.ngSitesBaseUrl}/s/${microsite.slug}`
              : `${config.ngSitesBaseUrl}/${projectData?.ID}/${item?.ID}`;
          return {
            id: item?.ID,
            name: item?.DocumentTemplate?.DisplayName || item?.DocumentTemplate?.OutputType || '',
            type: item?.DocumentTemplate?.OutputType,
            previewLink: `${config.assetServiceBaseUrl}/microsites/preview?slug=/${projectData?.ID}/${item?.ID}`, // this link should go through asset service
            siteUrl,
            lastUpdated: item?.UpdatedAt,
            queryID: item?.DocumentTemplate?.Query?.ID,
            microsite,
          };
        }
        return null;
      })
      .filter(Boolean);
  }, [projectData]);

  const publishValidationState: { disabled: boolean; message: string | null } = useDeepCompareMemo(() => {
    if (!isQueryValid) {
      return {
        disabled: true,
        message: isQueriesEnabled
          ? 'Please run a valid query before publishing microsites.'
          : 'Please complete all required fields in the Values form before publishing a microsite.',
      };
    }
    return { disabled: false, message: null };
  }, [isQueryValid]);

  const handleHomepageUpdate = async (type: string) => {
    try {
      const currentCustomPages: CustomPageEntry[] = Array.isArray(customPages) ? customPages : [];
      let updatedCustomPages: CustomPageEntry[];

      if (type === NavType.customPage) {
        const entry = { ID: projectData.ID, url: displayItem?.siteUrl || '', DisplayName: projectData.DisplayName };
        const exists = currentCustomPages.some((microsite) => microsite.ID === projectData.ID);

        updatedCustomPages = exists
          ? currentCustomPages.map((microsite) => (microsite.ID === projectData.ID ? entry : microsite))
          : [...currentCustomPages, entry];
      } else {
        updatedCustomPages = currentCustomPages.filter((microsite) => microsite.ID !== projectData.ID);
      }

      const payload: Record<string, any> = { custom_pages: updatedCustomPages };

      if (type === NavType.homepage) {
        payload['landing_page_url'] = displayItem?.siteUrl || null;
      } else if (landingPageUrl === displayItem?.siteUrl) {
        payload['landing_page_url'] = null;
      }

      await systemSettingsApi.updateSystemProfile(payload);
      dispatch(fetchSystemProfile());
    } catch {
      dispatch(addNotification({ type: 'error', message: 'Couldn\'t update the homepage setting. Please try again.' }));
    }
  };

  const handleMicrositeUpdate = async (item: MicrositeListItem | null, data: { [key: string]: any }) => {
    if (!item) {
      return;
    }

    const payload = {
      ...(item?.microsite || {}),
      ...data,
      ...('published' in data ? { publishedAt: data['published'] ? new Date().toISOString() : null } : {}),
    };

    try {
      await updateDocument({
        documentId: item.id,
        projectId: projectData.ID,
        data: { Microsite: JSON.stringify(payload) },
      }).unwrap();
    } catch (error: any) {
      const message = error?.data?.error || 'Couldn\'t save the microsite settings. Please try again.';
      if (message.includes('slug is already in use')) {
        setSlugError('This slug is already taken.');
      } else {
        dispatch(addNotification({ type: 'error', message }));
      }
    }
  };

  const checkSlugAvailability = async (slug: string, documentId: number): Promise<boolean> => {
    try {
      const response = await axios.get(
        `${config.assetServiceBaseUrl}/microsites/check-slug/${slug}?documentId=${documentId}`,
      );
      return response.data.available;
    } catch {
      return true;
    }
  };

  const openPanel = (item: MicrositeListItem) => {
    setEmailWhitelist(item.microsite?.emailWhitelist || []);
    setEmailInput('');
    setMicrositeSlug(item.microsite?.slug || `${projectData?.ID}/${item.id}`);
    setSlugError(null);
    setPanelItemId(item.id);
    setIsPanelOpen(true);
  };

  const closePanel = () => {
    setIsPanelOpen(false);
    setTimeout(() => setPanelItemId(null), 300);
  };

  const displayItem = micrositesList.find((item) => item.id === panelItemId) ?? null;

  const internalNavOptions: PublishWebsiteNavOption[] | null = isAdminUser
    ? [
        { type: NavType.none, label: 'None', description: 'Only accessible via the external link above.' },
        {
          type: NavType.customPage,
          label: 'Add to Internal Navigation',
          description: 'Appears under "Custom Page" in the left sidebar for all logged-in users.',
        },
        {
          type: NavType.homepage,
          label: 'Make Homepage',
          description: 'Replaces the default Clatter home screen for your organization.',
        },
      ]
    : null;

  const isPublished = displayItem?.microsite?.published;

  const buildAccessOptions = (item: MicrositeListItem): PublishWebsiteAccessOption[] => [
    {
      type: AccessType.customUrl,
      label: 'Public \u2014 Custom URL',
      description: 'Anyone with the link can view. Set a friendly, memorable URL for your client.',
      icon: <GlobeIcon />,
      body: (
        <>
          <StyledLinkLabel>Site URL</StyledLinkLabel>
          <StyledLinkBox>
            <StyledLinkPrefix>{getSitesDomain()}/</StyledLinkPrefix>
            <StyledSlugInput
              type="text"
              placeholder="acme_insurance"
              value={micrositeSlug}
              disabled={isLoading}
              onChange={(e) => {
                const val = e.target.value.replace(/[^a-zA-Z0-9_/]/g, '');
                setMicrositeSlug(val);
                setSlugError(null);
              }}
              onBlur={async () => {
                if (!micrositeSlug || !slugPattern.test(micrositeSlug)) {
                  return;
                }

                if (micrositeSlug === item.microsite?.slug) {
                  return;
                }

                const available = await checkSlugAvailability(micrositeSlug, item.id);
                if (!available) {
                  setSlugError('This slug is already taken.');
                  return;
                }

                handleMicrositeUpdate(item, {
                  customUrl: true,
                  privateLink: false,
                  slug: micrositeSlug,
                });
              }}
            />
            <NgInputCopyButton text={`${config.ngSitesBaseUrl}/${micrositeSlug}`} />
          </StyledLinkBox>
          <StyledHint>Letters, numbers, underscores, and slashes only.</StyledHint>
          {slugError && <StyledSlugError>{slugError}</StyledSlugError>}
        </>
      ),
      onPublish: async (navType: string) => {
        if (!micrositeSlug || !slugPattern.test(micrositeSlug)) {
          setSlugError('Please enter a valid slug.');
          return;
        }
        const available = await checkSlugAvailability(micrositeSlug, item.id);
        if (!available) {
          setSlugError('This slug is already taken.');
          return;
        }
        handleMicrositeUpdate(item, {
          protected: false,
          published: true,
          customUrl: true,
          privateLink: false,
          slug: micrositeSlug,
        });
        handleHomepageUpdate(navType);
      },
    },
    {
      type: AccessType.privateLink,
      label: 'Private Link \u2014 No Password',
      description: 'Auto-generated link that\u2019s practically impossible to guess. No login required.',
      icon: <LinkIcon />,
      body:
        isPublished && item.microsite?.privateLink && item.microsite?.slug ? (
          <>
            <StyledLinkLabel>Your private link</StyledLinkLabel>
            <StyledLinkBox>
              <StyledLinkPrefix>{getSitesDomain()}/s/</StyledLinkPrefix>

              <StyledLinkSlug>{item.microsite.slug}</StyledLinkSlug>

              <NgInputCopyButton text={`${config.ngSitesBaseUrl}/s/${item.microsite.slug}`} />
            </StyledLinkBox>
            <StyledHint>Generated on publish. Share only with intended recipients.</StyledHint>
          </>
        ) : (
          <StyledHint>A unique link will be generated when you publish.</StyledHint>
        ),
      onPublish: (navType: string) => {
        const token =
          item.microsite?.privateLink && item.microsite?.slug ? item.microsite.slug : generatePrivateToken();
        handleMicrositeUpdate(item, {
          protected: false,
          published: true,
          customUrl: false,
          privateLink: true,
          slug: token,
        });
        handleHomepageUpdate(navType);
      },
    },
    {
      type: AccessType.protected,
      label: 'One-Time Passcode',
      description: 'Recipients get a secure link and must enter a one-time code to view the site.',
      icon: <LockIcon />,
      body: (
        <>
          <StyledLinkLabel>Email / Domain</StyledLinkLabel>
          <StyledEmailInputRow>
            <StyledEmailInput
              type="text"
              placeholder="user@example.com or example.com"
              value={emailInput}
              disabled={isLoading}
              onChange={(e) => setEmailInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const trimmed = emailInput.trim();
                  if (trimmed && emailPattern.test(trimmed) && !emailWhitelist.includes(trimmed)) {
                    const updatedList = [...emailWhitelist, trimmed];
                    setEmailWhitelist(updatedList);
                    setEmailInput('');
                    handleMicrositeUpdate(item, { emailWhitelist: updatedList });
                  }
                }
              }}
            />
            <StyledAddButton
              disabled={isLoading || !emailInput.trim() || !emailPattern.test(emailInput.trim())}
              onClick={() => {
                const trimmed = emailInput.trim();
                if (trimmed && !emailWhitelist.includes(trimmed)) {
                  const updatedList = [...emailWhitelist, trimmed];
                  setEmailWhitelist(updatedList);
                  setEmailInput('');
                  handleMicrositeUpdate(item, { emailWhitelist: updatedList });
                }
              }}
            >
              Add
            </StyledAddButton>
          </StyledEmailInputRow>

          <AccessList
            list={emailWhitelist.map((email, index) => ({ ID: index, Name: email, Type: 'email' }))}
            onAction={(data: any) => {
              if (data?.type === 'REVOKE') {
                const updatedList = emailWhitelist.filter((email) => email !== data?.payload?.Name);
                setEmailWhitelist(updatedList);
                handleMicrositeUpdate(item, { emailWhitelist: updatedList });
              }
            }}
          />
        </>
      ),
      onPublish: (navType: string) => {
        handleMicrositeUpdate(item, {
          protected: true,
          published: true,
          customUrl: false,
          privateLink: false,
          slug: '',
          emailWhitelist,
        });
        handleHomepageUpdate(navType);
      },
    },
  ];

  const defaultNavType = useMemo(() => {
    if (displayItem?.siteUrl && landingPageUrl === displayItem.siteUrl) {
      return NavType.homepage;
    }

    if (displayItem && Array.isArray(customPages) && customPages.some((project) => project.ID === projectData?.ID)) {
      return NavType.customPage;
    }

    return NavType.none;
  }, [displayItem, landingPageUrl, customPages, projectData?.ID]);

  if (micrositesList?.length <= 0) {
    return null;
  }

  const defaultAccessType = displayItem?.microsite?.customUrl
    ? AccessType.customUrl
    : displayItem?.microsite?.privateLink
    ? AccessType.privateLink
    : displayItem?.microsite?.protected
    ? AccessType.protected
    : AccessType.customUrl;

  const panel = (
    <PublishWebsitePanel
      open={isPanelOpen}
      anchor={panelAnchor}
      title={displayItem ? `${projectData.DisplayName}` : ''}
      subtitle={isPublished ? 'Manage microsite access' : 'Configure access before going live'}
      isLoading={isLoading}
      previewLink={displayItem?.previewLink}
      internalNavOptions={internalNavOptions}
      isReady={!publishValidationState.disabled}
      isPublished={isPublished}
      accessOptions={displayItem ? buildAccessOptions(displayItem) : []}
      defaultAccessType={defaultAccessType}
      defaultNavType={defaultNavType}
      onClose={closePanel}
      onBack={closePanel}
      onUnpublish={() =>
        handleMicrositeUpdate(displayItem, {
          published: false,
          customUrl: false,
          privateLink: false,
          slug: '',
        })
      }
      onAccessTypeChange={(type) => {
        if (displayItem) {
          if (type === AccessType.protected) {
            setMicrositeSlug(`${projectData?.ID}/${displayItem.id}`);
            handleMicrositeUpdate(displayItem, { protected: true, customUrl: false, privateLink: false, slug: '' });
          } else if (type === AccessType.customUrl) {
            const defaultSlug = `${projectData?.ID}/${displayItem.id}`;
            setMicrositeSlug(
              displayItem.microsite?.slug && displayItem.microsite?.customUrl
                ? displayItem.microsite.slug
                : defaultSlug,
            );
            handleMicrositeUpdate(displayItem, {
              protected: false,
              customUrl: true,
              privateLink: false,
              slug: defaultSlug,
            });
          } else if (type === AccessType.privateLink) {
            const token = generatePrivateToken();
            handleMicrositeUpdate(displayItem, { protected: false, customUrl: false, privateLink: true, slug: token });
          } else {
            handleMicrositeUpdate(displayItem, { protected: false, customUrl: false, privateLink: false, slug: '' });
          }
        }
      }}
      onInternalNavChange={handleHomepageUpdate}
    />
  );

  if (buttonOnly) {
    return (
      <>
        {micrositesList.map((item) => {
          const { publishedAt } = item?.microsite || {};
          return (
            <StyledButtonOnlyWrapper key={item.id}>
              <NgButton
                width={buttonWidths.full}
                variant={item.microsite.published ? buttonVariants.clear : buttonVariants.primary}
                disabled={isLoading || publishValidationState?.disabled}
                onClick={() => openPanel(item)}
              >
                {item.microsite.published ? 'Manage →' : 'Configure & Publish →'}
              </NgButton>

              {(item.lastUpdated || publishedAt) && (
                <StyledButtonOnlyMeta>
                  {item.lastUpdated && <small>Date modified: {formatDate(item.lastUpdated)}</small>}
                  {publishedAt && <small>Date published: {formatDate(publishedAt)}</small>}
                </StyledButtonOnlyMeta>
              )}
            </StyledButtonOnlyWrapper>
          );
        })}
        {panel}
      </>
    );
  }

  return (
    <>
      <NgCard>
        <NgCardContent>
          <NgCardTitle title="Microsites" />
          {micrositesList.map((item) => {
            const { publishedAt } = item?.microsite || {};
            return (
              <StyledMicrositeRow key={item.id}>
                <TemplateLabel previewLink={item?.previewLink} name={item?.name}>
                  <StyledMicrositeLabelMeta>
                    <small>Date modified: {formatDate(item.lastUpdated)}</small>
                    {!!publishedAt && <small>Date published: {formatDate(publishedAt)}</small>}
                  </StyledMicrositeLabelMeta>
                </TemplateLabel>

                <NgButton
                  width={buttonWidths.full}
                  variant={item.microsite.published ? buttonVariants.clear : buttonVariants.primary}
                  disabled={isLoading || publishValidationState?.disabled}
                  onClick={() => openPanel(item)}
                >
                  {item.microsite.published ? 'Manage \u2192' : 'Configure & Publish \u2192'}
                </NgButton>
              </StyledMicrositeRow>
            );
          })}

          <NgErrorMessage message={publishValidationState?.message} />
        </NgCardContent>
      </NgCard>

      {panel}
    </>
  );
};
